import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/Badge.js";
import { Banner } from "../../components/ui/Banner.js";
import { Button } from "../../components/ui/Button.js";
import { Card } from "../../components/ui/Card.js";
import { ProgressBar } from "../../components/ui/ProgressBar.js";
import { StanBledu, StanLadowania, StanPusty } from "../../components/ui/stany.js";
import { useListaSesji } from "../../hooks/useListaSesji.js";
import type { SessionListEntry } from "../../lib/api.js";
import { odmien } from "../../lib/polski.js";
import {
  dataWiersza,
  etykietaStatusuUzytkownika,
  formatujDlugosc,
  postepWiersza,
  wariantStatusu,
} from "../../lib/wierszListy.js";

/**
 * Ekran startowy konta `user` ("Moje rozmowy"): jedna duza akcja "Oceń rozmowę" i lista dotychczasowych rozmow.
 *
 * Lista nie pokazuje oceny w wierszu, tylko plakietke statusu (gotowe, oceniamy, do zatwierdzenia, nie udalo sie): wynik
 * jest na ekranie rozmowy, a lista ma tylko pomoc w znalezieniu jej. Przy trwajacej ocenie jest pasek postepu, a przycisk
 * mowi "Zobacz postęp" zamiast "Otwórz". Tresci bledu z przetwarzania uzytkownik nie dostaje nigdy: serwer podmienia je
 * na jedno ogolne zdanie, a ten widok i tak ich nie wypisuje.
 *
 * Odpytywanie, stany ladowania, bledu i pusty: jak lista administratora (hooks/useListaSesji.ts). Blad cichego odswiezenia
 * to baner, a nie pusta lista (S-7, P-6).
 */

function opisWiersza(s: SessionListEntry): string {
  return [
    s.inputKind === "TEXT" ? "tekst" : (formatujDlugosc(s.durationSec) ?? "nagranie"),
    dataWiersza(s.createdAt),
    s.utteranceCount > 0
      ? `${s.utteranceCount} ${odmien(s.utteranceCount, "wypowiedź", "wypowiedzi", "wypowiedzi")}`
      : null,
  ]
    .filter((c): c is string => c !== null)
    .join(" · ");
}

function WierszRozmowy({ s }: { s: SessionListEntry }): React.JSX.Element {
  const { wToku, procent } = postepWiersza(s);

  return (
    <li className="rowlist__row start-wiersz">
      <div className="rowlist__body">
        <p className="rowlist__nazwa">
          <Link className="sesja-link" to={`/analizy/${s.id}`}>
            {s.title}
          </Link>
        </p>
        <p className="rowlist__meta">{opisWiersza(s)}</p>
        {/* Czekanie na zatwierdzenie podzialu nie ma paska: nic sie nie liczy, a pasek sugerowalby prace, ktorej nie ma. */}
        {wToku && (
          <div className="start-wiersz__postep">
            <ProgressBar etykieta="Postęp oceny" procent={procent} />
          </div>
        )}
      </div>

      <div className="start-wiersz__status">
        <Badge wariant={wariantStatusu(s.status)} kropka>
          {etykietaStatusuUzytkownika(s.status)}
        </Badge>
        <Link className="btn btn--secondary" to={`/analizy/${s.id}`} aria-label={`${wToku ? "Zobacz postęp" : "Otwórz"}: ${s.title}`}>
          {wToku ? "Zobacz postęp" : "Otwórz"}
        </Link>
      </div>
    </li>
  );
}

export function StartPage(): React.JSX.Element {
  const { sesje, bladListy, bladOdswiezania, pobierz } = useListaSesji();

  return (
    <>
      <h1 className="page-title">Moje rozmowy</h1>
      <p className="page-lead">Wgraj rozmowę, a pokażemy, co w niej wyszło dobrze i co warto poprawić.</p>

      {/* BN-1: trwajacy STAN, nie zdarzenie - baner znika sam, gdy odswiezenie znowu sie uda. */}
      {bladOdswiezania && (
        <Banner
          wariant="warning"
          tekst="Nie udaje się odświeżyć listy. Pokazujemy ostatnie dane, jakie mamy."
          akcja={
            <Button wariant="secondary" onClick={() => void pobierz(true)}>
              Odśwież
            </Button>
          }
        />
      )}

      <Card>
        <div className="start-hero">
          {/* Tylko tekst: konto `user` nie wgrywa nagran (docs/konta.md, "Nagrania kont uzytkownikow"). */}
          <p className="start-hero__tekst">
            Wklej tekst albo wgraj plik z tekstem rozmowy. Ocena trwa zwykle kilka minut.
          </p>
          {/* B-6: jedyne wypelnienie marki - akcja, ktora uruchamia ocene. To nawigacja do formularza, wiec link. */}
          <Link className="btn-hero" to="/nowa">
            Oceń rozmowę
          </Link>
        </div>
      </Card>

      <Card tytul="Ostatnie rozmowy">
        {bladListy !== null ? (
          <StanBledu
            tytul="Nie udało się wczytać rozmów"
            opis="Serwer nie odpowiedział. Spróbuj ponownie za chwilę."
            akcja={
              <Button wariant="secondary" onClick={() => void pobierz(false)}>
                Spróbuj ponownie
              </Button>
            }
          />
        ) : sesje === null ? (
          <StanLadowania tekst="Wczytuję rozmowy..." />
        ) : sesje.length === 0 ? (
          <StanPusty
            tytul="Nie ma jeszcze żadnej rozmowy"
            opis="Kliknij „Oceń rozmowę”, a wynik pojawi się na tej liście."
          />
        ) : (
          /* T-5: jedna polaczona lista w ramce, wiersze rozdzielone hairline'ami. */
          <ul className="rowlist">
            {sesje.map((s) => (
              <WierszRozmowy s={s} key={s.id} />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
